import { createLogger } from '@app/utils/loggerCreator'
import { sink } from '@app/utils/sink'

import type { ChatServerApi, ChatServerApiOptions } from '@app/packages/chat/Api'

import { getChatToken } from '@app/store/actions/chatToken'
import { profileChatUidSelector } from '@app/store/selectors/profile'
import type { ChatConfig } from '@app/store/slices/app_config'
import { applyChatAction, resolveParticipants } from '@app/store/slices/chat/actions'
import { localeSelector } from '@app/store/slices/locale'
import type { Store } from '@app/store/store'
import { createThunk } from '@app/store/thunk'

/** Store bindings the package `ChatServerApi` needs (token, config url, profile, dispatch). */
export function chatApiOptions(store: Pick<Store, 'dispatch' | 'getState'>, config: ChatConfig): ChatServerApiOptions {
  const log = createLogger('ChatServerApi', 'teal', 'bold')
  return {
    getBaseUrl: () => config.apiUrl,
    getToken: async () => (await store.dispatch(getChatToken())).access_token,
    getSelfUid: () => profileChatUidSelector(store.getState()),
    getLocale: () => localeSelector(store.getState()),
    dispatch: action => {
      store.dispatch(applyChatAction(action))
    },
    resolveParticipants: externalIds => {
      void store.dispatch(resolveParticipants(externalIds))
    },
    log: (...args) => log.log(...args),
  }
}

/** Store-bound chat_server REST facade, set up front by ChatLoader (see setChatApi). */
export function getChatApi() {
  return createThunk((_dispatch, _getState, context) => context.lookupEnsure<ChatServerApi>(ChatApiSymbol))
}

/** Like getChatApi, but null before ChatLoader registers the facade (early renders, tests). */
export function getChatApiSafe() {
  return createThunk((_dispatch, _getState, context) => context.lookup<ChatServerApi>(ChatApiSymbol) ?? null)
}

/**
 * The facade, waiting for `ChatLoader` when it is still booting — it registers only after
 * app_config resolves and the chat chunk loads, and a support widget opened before that used to
 * throw (FRONTEND-91Z). Where chat never boots at all this throws exactly as `getChatApi` does.
 */
export function awaitChatApi() {
  return createThunk(async (dispatch, _getState, context) => {
    const api = context.lookup<ChatServerApi>(ChatApiSymbol)
    if (api) return api

    const pending = dispatch(getChatApiPromise())
    if (!pending) return context.lookupEnsure<ChatServerApi>(ChatApiSymbol)

    return await pending
  })
}

/** `ChatLoader`'s promise of the facade, registered before it starts awaiting. */
export function getChatApiPromise() {
  return createThunk((_dispatch, _getState, context) => context.lookup<Promise<ChatServerApi>>(ChatApiPromiseSymbol) ?? null)
}

export function setChatApiPromise(value: Promise<ChatServerApi>) {
  sink(value)
  return createThunk((_dispatch, _getState, context) => context.getRootContext().set(ChatApiPromiseSymbol, value))
}

/** Test seam: pre-set a (stub) facade before components ask for one. */
export function setChatApi(value: ChatServerApi) {
  return createThunk((_dispatch, _getState, context) => context.getRootContext().set(ChatApiSymbol, value))
}

const ChatApiSymbol = Symbol('ChatServerApi')
const ChatApiPromiseSymbol = Symbol('ChatServerApiPromise')
